import axios, {AxiosResponse} from 'axios';
import { EmptyResponse } from '@/models/index';
import * as utils from '@/apis/apiUtils';
import type {
  HokkaidoAirportReportIndexParams,
  HokkaidoAirportReportCreateParams,
  HokkaidoAirportReportStartParams,
  HokkaidoAirportReportEndParams,
  HokkaidoAirportReportContentAndRoadParams,
  HokkaidoAirportReportBaseArrivalTsUpdateParams,
  HokkaidoAirportReportPhotoParams,
  HokkaidoAirportReportPreWorkInspectionParams,
  HokkaidoAirportReportPreWorkInspectionSkipParams,
  HokkaidoAirportReportPostWorkInspectionParams,
  HokkaidoAirportReportPostWorkInspectionSkipParams,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportReportRequest';
import type {
  HokkaidoAirportReportPhoto,
  HokkaidoAirportReportResponse,
  HokkaidoAirportReportPhotoOrderResponse,
  HokkaidoAirportReportPhotoCountResponse,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportReportResponse';
import type {
  HokkaidoAirportReport,
  HokkaidoAirportReportPhotoCount,
} from '@/models/externalReports/hokkaidoAirport/spHokkaidoAirportReport';
import type {
  HokkaidoAirportReportWithPhotos,
  HokkaidoAirportPhotoOrder,
  HokkaidoAirportReportPhotoExt,
} from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportReport';

export default {
  async index(params?: HokkaidoAirportReportIndexParams): Promise<HokkaidoAirportReportWithPhotos[]> {
    const { data } = await axios.get<HokkaidoAirportReportResponse[]>(
      utils.getApiUrl('/external_reports/hokkaido-airport/reports'),
      {params: params || {}},
    );
    return convertHokkaidoAirportReportsResponseToHokkaidoAirportReports(data);
  },
  getPhotoOrders(reportId: number): Promise<AxiosResponse<HokkaidoAirportReportPhotoOrderResponse[]>> {
    return axios.get(utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${reportId}/photos/order`));
  },
  async getPhotoCount(id: number): Promise<HokkaidoAirportReportPhotoCount[]> {
    const { data } = await axios.get<HokkaidoAirportReportPhotoCountResponse>(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}/photo_count`),
    );
    return convertPhotoCountResponseToPhotoCount(data);
  },
  async get(id: number): Promise<HokkaidoAirportReportWithPhotos> {
    const { data } = await axios.get<HokkaidoAirportReportResponse>(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}`),
    );
    return convertHokkaidoAirportReportsResponseToHokkaidoAirportReports([data])[0];
  },
  async createHokkaidoAirportReport(params: HokkaidoAirportReportCreateParams): Promise<HokkaidoAirportReport> {
    const { data } = await axios.post<HokkaidoAirportReportResponse>(
      utils.getApiUrl('/external_reports/hokkaido-airport/reports'),
      params,
    );
    return convertHokkaidoAirportReportResponseToHokkaidoAirportReport(data);
  },
  async updateHokkaidoAirportReportStart(
    id: number,
    params: HokkaidoAirportReportStartParams,
  ): Promise<HokkaidoAirportReport> {
    const { data } = await axios.post<HokkaidoAirportReportResponse>(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}/start_time`),
      params,
    );
    return convertHokkaidoAirportReportResponseToHokkaidoAirportReport(data);
  },
  async updateHokkaidoAirportReportEnd(
    id: number,
    params: HokkaidoAirportReportEndParams,
  ): Promise<HokkaidoAirportReport> {
    const { data } = await axios.post<HokkaidoAirportReportResponse>(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}/end_time`),
      params,
    );
    return convertHokkaidoAirportReportResponseToHokkaidoAirportReport(data);
  },
  async updateBaseArrival(
    id: number,
    params: HokkaidoAirportReportBaseArrivalTsUpdateParams,
  ): Promise<HokkaidoAirportReport> {
    const { data } = await axios.post<HokkaidoAirportReportResponse>(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}/base_arrival_time`),
      params,
    );
    return convertHokkaidoAirportReportResponseToHokkaidoAirportReport(data);
  },
  async completePreWorkInspection(
    id: number,
    params: HokkaidoAirportReportPreWorkInspectionParams,
  ): Promise<HokkaidoAirportReport> {
    const { data: reportResponse } = await axios.post<HokkaidoAirportReportResponse>(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}/pre_work_inspection`),
      params,
    );
    return convertHokkaidoAirportReportResponseToHokkaidoAirportReport(reportResponse);
  },
  async skipPreWorkInspection(
    id: number,
    params: HokkaidoAirportReportPreWorkInspectionSkipParams,
  ): Promise<HokkaidoAirportReport> {
    const { data: reportResponse } = await axios.post<HokkaidoAirportReportResponse>(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}/pre_work_inspection/skip`),
      params,
    );
    return convertHokkaidoAirportReportResponseToHokkaidoAirportReport(reportResponse);
  },
  async completePostWorkInspection(
    id: number,
    params: HokkaidoAirportReportPostWorkInspectionParams,
  ): Promise<HokkaidoAirportReport> {
    const { data: reportResponse } = await axios.post<HokkaidoAirportReportResponse>(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}/post_work_inspection`),
      params,
    );
    return convertHokkaidoAirportReportResponseToHokkaidoAirportReport(reportResponse);
  },
  async skipPostWorkInspection(
    id: number,
    params: HokkaidoAirportReportPostWorkInspectionSkipParams,
  ): Promise<HokkaidoAirportReport> {
    const { data: reportResponse } = await axios.post<HokkaidoAirportReportResponse>(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}/post_work_inspection/skip`),
      params,
    );
    return convertHokkaidoAirportReportResponseToHokkaidoAirportReport(reportResponse);
  },
  async updateHokkaidoAirportReportContentAndRoad(
    id: number,
    params: HokkaidoAirportReportContentAndRoadParams,
  ): Promise<HokkaidoAirportReport> {
    const { data } = await axios.post<HokkaidoAirportReportResponse>(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}`),
      params,
    );
    return convertHokkaidoAirportReportResponseToHokkaidoAirportReport(data);
  },
  async addPhoto(id: number, params: HokkaidoAirportReportPhotoParams): Promise<HokkaidoAirportReportPhotoExt> {
    const formData = utils.transformFormData(params);
    const { data } = await axios.post<HokkaidoAirportReportPhoto>(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}/photos`),
      formData,
    );
    return convertReportPhotosResponseToReportPhotos(data);
  },
  updatePhotosOrder(id: number, newOrders: HokkaidoAirportPhotoOrder[]): Promise<AxiosResponse<EmptyResponse>> {
    return axios.post(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}/photos/order`),
      {new_orders: newOrders},
    );
  },
  updatePhotoComment(id: number, photoId: number, comment: string): Promise<AxiosResponse<HokkaidoAirportReportPhoto>> {
    return axios.post(
      utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}/photos/${photoId}/comment`),
      {comment: comment},
    );
  },
  deletePhoto(id: number, photoId: number): Promise<AxiosResponse<EmptyResponse>> {
    return axios.delete(utils.getApiUrl(`/external_reports/hokkaido-airport/reports/${id}/photos/${photoId}`));
  },
};

export function convertHokkaidoAirportReportResponseToHokkaidoAirportReport(
  hokkaidoAirportReport: HokkaidoAirportReportResponse,
): HokkaidoAirportReport {
  return {
    id: hokkaidoAirportReport.id,
    group1Id: hokkaidoAirportReport.group1_id,
    hokkaidoAirportCarId: hokkaidoAirportReport.external_reports_hokkaido_airport_car_id,
    baseDepartureTs: new Date(hokkaidoAirportReport.base_departure_ts),
    operationStartTs: hokkaidoAirportReport.operation_start_ts
      ? new Date(hokkaidoAirportReport.operation_start_ts)
      : null,
    operationEndTs: hokkaidoAirportReport.operation_end_ts ? new Date(hokkaidoAirportReport.operation_end_ts) : null,
    baseArrivalTs: hokkaidoAirportReport.base_arrival_ts ? new Date(hokkaidoAirportReport.base_arrival_ts) : null,
    hanName: hokkaidoAirportReport.han_name,
    airportName: hokkaidoAirportReport.airport_name ?? '',
    targetFacility: hokkaidoAirportReport.target_facility ?? '',
    workAreas: hokkaidoAirportReport.work_areas ?? '',
    workCycles: (hokkaidoAirportReport.work_cycles ?? []).map(workCycle => ({
      repeatCount: workCycle.repeat_count,
      startTime: new Date(workCycle.start_time),
      endTime: workCycle.end_time ? new Date(workCycle.end_time) : null,
    })),
    carType: hokkaidoAirportReport.car_type ?? '',
    standbyType: hokkaidoAirportReport.standby_type ?? '',
    preWorkInspectionTs: parseNullableDate(hokkaidoAirportReport.pre_work_inspection_ts),
    preWorkInspectionResults: hokkaidoAirportReport.pre_work_inspection_results,
    preWorkInspectionSkipped: hokkaidoAirportReport.pre_work_inspection_skipped,
    postWorkInspectionTs: parseNullableDate(hokkaidoAirportReport.post_work_inspection_ts),
    postWorkInspectionResults: hokkaidoAirportReport.post_work_inspection_results,
    postWorkInspectionSkipped: hokkaidoAirportReport.post_work_inspection_skipped,
  };
}

const parseNullableDate = (value: string | null): Date | null => {
  return value ? new Date(value) : null;
};

function convertPhotoCountResponseToPhotoCount(
  response: HokkaidoAirportReportPhotoCountResponse,
): HokkaidoAirportReportPhotoCount[] {
  return response.photo_counts.map(item => ({
    photoType: item.photo_type,
    count: item.count,
  }));
}

export function convertHokkaidoAirportReportsResponseToHokkaidoAirportReports(
  response: HokkaidoAirportReportResponse[],
): HokkaidoAirportReportWithPhotos[] {
  return response.map(item => ({
    ...convertHokkaidoAirportReportResponseToHokkaidoAirportReport(item),
    photos: item.photos.map(photo => convertReportPhotosResponseToReportPhotos(photo)),
  }));
}

export function convertReportPhotosResponseToReportPhotos(response: HokkaidoAirportReportPhoto): HokkaidoAirportReportPhotoExt {
  return {
    id: response.id,
    ts: response.ts,
    lat: Number(response.lat),
    lon: Number(response.lon),
    photoType: response.photo_type,
    imagePath: response.image_path,
    dispOrder: response.disp_order,
    airportName: '',
    standbyType: '',
    comment: response.comment,
    isSelected: false,
    hanName: '',
    savedImage: null,
    samePosPhotos: [],
  };
}
